import React, { MouseEvent, ReactNode } from 'react';
import EditorContainer from './editor/EditorContainer';
import RuleAccessComponent from './people/RuleAccessComponent';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../constant/CollectionNames';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import { RuleJsonModel } from '../../domain/collection/rule/FlowDomains';
import RuleService from '../../service/RuleService';
import CustomLoading from '../custom/CustomLoading';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import { RootState } from '../../redux/store';
import { addPagePath, changePagePath, clearPage } from '../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { changeRule, changeRuleDirty, changeRuleIsUpdate, clearRule } from '../../redux/actions/RuleActions';
import ComponentName from '../../constant/ComponentName';
import { enterComponent, leaveComponent } from '../../redux/actions/ComponentActions';
import CustomTabv2, { CustomTabItem } from '../custom/CustomTabv2';
import CustomEditorItemComponent from '../custom/CustomEditorItemComponent';
import { isStringValid } from '../../util/Generator';
import MessageUtil from '../../util/MessageUtil';
import LanguageUtil from '../../util/LanguageUtil';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../deploy/UndeployImpactDialog';
import RoleHelper from '../../RoleHelper';
import ComponentAuditLog from '../ComponentAuditLog';
import Breadcrumb from '../Breadcrumb';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../helpers/StandardEditorContainerComponent';
import ComponentErrorLog from '../ComponentErrorLog';
import { EnrichedComponentCategory } from '../../domain/category/CategoryOperationDomains';
import { AuditLog } from '../../domain/rbac/RbacDomains';
import eq = query.eq;
import { ConfirmPopup } from 'primereact/confirmpopup';
import { RuleTypes } from '../../domain/collection/rule/RuleDomains';

class RuleEditorContainer extends StandardEditorContainerComponent<Props, State, RuleJsonModel> {
  public state = new State();

  private flowSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private initialDirtyResetTimer: ReturnType<typeof setTimeout> | undefined;
  private isInitialDirtyResetScheduled: boolean = false;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly flowService: RuleService = ServiceContainer.getInstance().get(ServiceNames.FLOW);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.RULE,
      icon: 'rule',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const { loading } = this.state;
    const {
      rule: { rule, isUpdate, isDirty },
    } = this.props;
    if (loading || rule == undefined) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={rule.name}
          description={rule.description || ''}
          status={rule?.stage || 'DRAFT'}
          icon={'enrichment'}
          isUpdate={isUpdate}
          onSave={this.saveRule}
          onDeploy={this.deployRule}
          onUndeploy={this.undeployRule}
          onRemove={this.removeRule}
          onChangeName={this.onChangeRuleName}
          onChangeDescription={this.onChangeRuleDescription}
          disableSave={!RoleHelper.canEdit('Rule')}
          disableRemove={!RoleHelper.canEdit('Rule')}
          disableNameChange={!RoleHelper.canEdit('Rule')}
          disableDescriptionChange={!RoleHelper.canEdit('Rule')}
          disableDeploy={!RoleHelper.canDeploy('Rule') || isDirty}
          disableUndeploy={!RoleHelper.canDeploy('Rule')}
          component={ComponentName.RULE}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full py-3 '}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearRule, router: { location } } = this.props;
    const pathParts = location.pathname.split('/');
    const type = pathParts.includes(RuleTypes.RESOLUTION) ? RuleTypes.RESOLUTION : RuleTypes.MONITORING;
    if (this.initialDirtyResetTimer) {
      clearTimeout(this.initialDirtyResetTimer);
    }
    clearRule(type);
    await super.componentWillUnmount();
  }

  public componentDidUpdate(prevProps: Props) {
    const {
      rule: { isUpdate },
      changeRuleDirty,
    } = this.props;
    if (!prevProps.rule.isUpdate && isUpdate && !this.isInitialDirtyResetScheduled) {
      this.isInitialDirtyResetScheduled = true;
      this.initialDirtyResetTimer = setTimeout(() => {
        changeRuleDirty(false);
      }, 1200);
    }
  }

  protected _startTracker = () => {
    const flowId = this.getItemIdFromRoute();
    if (flowId == undefined) {
      this.setState({ loading: false });
    }
    const flowQuery = query.QueryBuilder.select().from(CollectionNames.RULES).where(eq('id', flowId)).build();
    this.flowSubscription = subscribe.Formica.subscribe(CollectionNames.RULES, flowQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.flowSubscription]);
  };

  protected tracker = async () => {
    const { changeRule, changeRuleIsUpdate, changeRuleDirty } = this.props;
    if (this.flowSubscription?.ready()) {
      const flowId = this.getItemIdFromRoute();
      if (flowId == undefined) {
        changeRuleIsUpdate(false);
        this.setState({ loading: false });
        RoleHelper.setCurrentAccess('Edit');
        changeRuleDirty(false);
        return;
      }
      const flowQuery = query.QueryBuilder.select().from(CollectionNames.RULES).where(eq('id', flowId)).build();
      const rule: RuleJsonModel | undefined = await this.repository.find(flowQuery);
      if (rule == undefined) {
        MessageUtil.error({ summary: 'Rule not found, your will be redirect to rule list page in 3 seconds' });
        const {
          router: { navigate },
        } = this.props;
        setTimeout(() => navigate('/rule'), 3000);
      }
      changeRule(rule || null);
      changeRuleIsUpdate(true);
      RoleHelper.setCurrentAccess(rule);
      this.setState({ loading: false });
      changeRuleDirty(false);
    }
  };

  protected _stopTracker = async () => {
    await this.flowSubscription?.stop();
    this.computation?.stop();
  };

  protected async _preparePreview(log: AuditLog): Promise<void> {
    const {
      changeRule,
      changeRuleIsUpdate,
    } = this.props;
    const rawDocument = log.document.data as unknown;
    const document = typeof rawDocument === 'string' ? JSON.parse(rawDocument) : rawDocument;
    if (document == undefined || typeof document != 'object') {
      console.error('Document is not valid', document);
      return;
    }
    changeRule(document as RuleJsonModel);
    changeRuleIsUpdate(true);
    this.setState({ loading: false });
  }

  private onChangeRuleName = (value: string) => {
    const {
      rule: { rule },
      changeRule,
      changeRuleDirty,
    } = this.props;
    if (isStringValid(value)) {
      rule.name = value;
      changeRule(rule);
      changeRuleDirty(true);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeRuleDescription = (value: string) => {
    const {
      rule: { rule },
      changeRule,
      changeRuleDirty,
    } = this.props;
    rule.description = value;
    changeRule(rule);
    changeRuleDirty(true);
  };

  private switchToUpdate = async () => {
    const {
      rule: { rule },
      enterComponent,
    } = this.props;
    const {
      router: { navigate, params },
    } = this.props;
    if (rule == undefined || rule.name == '') return;
    enterComponent({ component: ComponentName.RULE, id: rule.name });
    navigate(`/rule${params.folderId ? `/folder/${params.folderId}` : ''}/edit/${rule.name}`, { replace: true });
    setTimeout(() => {
      this.computation?.onStop(() => this.startTracker());
      this.stopTracker();
    });
  };

  private saveRule = async () => {
    const {
      page: { lastCategory },
      rule: { rule, isUpdate },
      changeRuleIsUpdate,
    } = this.props;
    if (rule == undefined) {
      return;
    }
    try {
      const isSaved = await this.flowService.saveFlow(rule, isUpdate, lastCategory);
      if (isSaved) {
        if (!isUpdate) await this.switchToUpdate();
        changeRuleIsUpdate(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  private deployRule = async (e: MouseEvent<HTMLElement>) => {
    const {
      rule: { rule },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.flowService.saveFlow(rule, true, {} as EnrichedComponentCategory);
        await this.flowService.deployFlow(rule);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
    });
  };

  private undeployRule = async (e: MouseEvent<HTMLElement>) => {
    const {
      rule: { rule },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => confirmUndeployWithImpact(ComponentName.RULE, [rule.name], () => this.flowService.undeployFlow(rule.name)),
      message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
    });
  };

  private removeRule = (e: MouseEvent<HTMLElement>) => {
    const {
      rule: { rule },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        try {
          const success = await this.flowService.removeFlow(rule.name);
          if (success) {
            navigate(`/rule`);
          }
        } catch (e: any) {
          console.error(e);
        }
      },
      message: LanguageUtil.get({ id: 'messages.delete1', defaultMessage: 'Are you sure you want to remove?' }),
    });
  };

  protected getTabs(): CustomTabItem[] {
    return [
      {
        label: LanguageUtil.get({ id: 'common.tabs.editor', defaultMessage: 'Editor' }),
        key: 'Editor',
        content: <EditorContainer />,
      },
      {
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        key: 'Access',
        content: <RuleAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.RULE, true),
      },
      {
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        key: 'Audit',
        content: <ComponentAuditLog component={ComponentName.RULE} documentName={this.props.rule.rule.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.RULE, true) && this.environmentUtil.isAuditLogVisible(),
      },
      {
        key: 'Error',
        label: LanguageUtil.get({ id: 'common.tabs.error', defaultMessage: 'Error' }),
        content: <ComponentErrorLog component={ComponentName.RULE} documentName={this.props.rule.rule.name} />,
        visible: RoleHelper.canManage(ComponentName.RULE, true),
      },
    ];
  }

  protected changeItem = (item: RuleJsonModel) => {
    const { changeRule } = this.props;
    changeRule(item);
  };
}

interface Props extends FormicaProps, ReduxProps {}

class State {
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  page: state.page,
  rule: state.rule,
});
const mapDispatchToProps = {
  addPagePath,
  changePagePath,
  changeRule,
  clearPage,
  changeRuleIsUpdate,
  clearRule,
  enterComponent,
  leaveComponent,
  changeRuleDirty,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(RuleEditorContainer));
